/**
 * Interactive attribution funnel — shows the CTWA journey from impressions to
 * purchases as fixed-height horizontal bands, width proportional to value with
 * a minimum visible width so real drop-off rates (4%, 0.4%…) stay readable.
 * Zero renders as zero (empty track, explicit "0"); steps a channel doesn't
 * track render as "n/a — not tracked" instead of a fake number. Band colors
 * come from the brand-indigo ordinal ramp in funnelRamp.ts; AI highlights take
 * the warning token.
 */

import { useId, useState } from 'react'
import type { CSSProperties } from 'react'
import { readCssVar } from './nivoTheme'
import { useFunnelRamp } from './funnelRamp'
import { formatMarketingCents } from '../format'

export interface FunnelStep {
  id: string
  label: string
  value: number
  cost?: number
  /** The data source for this step doesn't exist on this channel (e.g. Google
   * campaigns have no lead/purchase tracking) — renders "n/a — not tracked"
   * instead of a dishonest 0. */
  untracked?: boolean
}

interface AttributionFunnelProps {
  data: FunnelStep[]
  highlights?: string[]
  height?: number
  showConversionRates?: boolean
  onStepClick?: (step: FunnelStep) => void
}

/** Non-zero bands never drop below this share of the track, so sub-percent
 * stages stay visible (and clickable) instead of collapsing to a sliver. */
const MIN_BAND_PCT = 2

// Fallback when --warning is unresolvable (SSR / tests): light-mode amber.
const WARNING_FALLBACK = '#d97706'

function formatNumber(n: number): string {
  return n.toLocaleString()
}

/** Stage-to-stage rate for the strip: n/a when either side isn't tracked,
 * an em dash when the previous stage is zero (0/0 is not "0.0%"). */
function conversionRate(prev: FunnelStep, step: FunnelStep): string {
  if (prev.untracked || step.untracked) return 'n/a'
  if (prev.value <= 0) return '—'
  return `${((step.value / prev.value) * 100).toFixed(1)}%`
}

export function AttributionFunnel({
  data,
  highlights,
  height = 320,
  showConversionRates = true,
  onStepClick,
}: AttributionFunnelProps) {
  const ramp = useFunnelRamp(data.length)
  const [activeIdx, setActiveIdx] = useState<number | null>(null)
  const tooltipIdBase = useId()

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        No funnel data available
      </div>
    )
  }

  const warningColor = readCssVar('--warning') || WARNING_FALLBACK
  const tracked = data.filter((s) => !s.untracked)
  // Widths scale against the largest tracked stage; the "% of total" readout
  // anchors on the top tracked stage — when that stage is 0 the line is
  // suppressed rather than re-anchored on a later stage (which would read
  // ">100% of total").
  const maxValue = Math.max(0, ...tracked.map((s) => s.value))
  const totalValue = tracked[0]?.value ?? 0

  const conversionRates = showConversionRates && data.length > 1
    ? data.slice(1).map((step, i) => ({
        from: data[i].label,
        to: step.label,
        rate: conversionRate(data[i], step),
        id: step.id,
      }))
    : null

  const bandWidthPct = (step: FunnelStep): number => {
    if (step.untracked || step.value <= 0 || maxValue <= 0) return 0
    return Math.round(Math.max((step.value / maxValue) * 100, MIN_BAND_PCT) * 100) / 100
  }

  const renderTooltip = (step: FunnelStep, idx: number) => {
    const prev = idx > 0 ? data[idx - 1] : null
    const rate = prev && !prev.untracked && !step.untracked && prev.value > 0
      ? ((step.value / prev.value) * 100).toFixed(1)
      : null
    return (
      <div
        role="tooltip"
        id={`${tooltipIdBase}-${idx}`}
        className="absolute left-0 top-full z-10 mt-1 w-max max-w-60 pointer-events-none bg-popover rounded-lg shadow-lg px-3 py-2 text-sm border border-border"
      >
        <p className="font-semibold">{step.label}</p>
        {step.untracked ? (
          <p className="text-muted-foreground text-xs">Not tracked for this channel</p>
        ) : (
          <>
            <p className="text-muted-foreground">{formatNumber(step.value)}</p>
            {step.cost != null && step.cost > 0 && (
              <p className="text-muted-foreground text-xs">Cost: {formatMarketingCents(step.cost)}</p>
            )}
            {totalValue > 0 && (
              <p className="text-muted-foreground text-xs">
                {((step.value / totalValue) * 100).toFixed(1)}% of total
              </p>
            )}
            {rate != null && showConversionRates && (
              <p className="text-info text-xs">{rate}% from previous stage</p>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Pipeline conversion summary — above the funnel with clear spacing */}
      {conversionRates && (
        <div className="flex justify-center gap-5 mb-4 flex-wrap">
          {conversionRates.map((cr) => (
            <div key={cr.id} className="flex items-center gap-1.5 text-sm">
              <span className="text-foreground font-medium">{cr.from}</span>
              <span className="text-muted-foreground">→</span>
              <span className="text-foreground font-medium">{cr.to}</span>
              <span className="text-primary font-bold ml-0.5">{cr.rate}</span>
            </div>
          ))}
        </div>
      )}

      {/* Segmented funnel body */}
      <div
        data-testid="funnel-body"
        role="list"
        className="flex flex-col justify-evenly"
        style={{ height }}
      >
        {data.map((step, idx) => {
          const widthPct = bandWidthPct(step)
          const color = highlights?.includes(step.id) ? warningColor : ramp[idx]
          const valueText = step.untracked ? 'n/a — not tracked' : formatNumber(step.value)
          const rowProps = {
            'aria-label': `${step.label}: ${valueText}`,
            'aria-describedby': activeIdx === idx ? `${tooltipIdBase}-${idx}` : undefined,
            className: 'group relative block w-full text-left',
            onPointerEnter: () => setActiveIdx(idx),
            onPointerLeave: () => setActiveIdx(null),
            onFocus: () => setActiveIdx(idx),
            onBlur: () => setActiveIdx(null),
          }
          const rowContent = (
            <>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-sm font-medium text-foreground truncate">{step.label}</span>
                <span
                  className={
                    step.untracked
                      ? 'text-xs italic text-muted-foreground'
                      : 'text-sm font-semibold text-foreground tabular-nums'
                  }
                >
                  {valueText}
                </span>
              </div>
              <div className="relative h-6 rounded bg-muted/60 overflow-hidden">
                {widthPct > 0 && (
                  <div
                    data-testid={`funnel-band-${step.id}`}
                    className="absolute inset-y-0 left-0 rounded-r-[4px] motion-safe:transition-[width] motion-safe:duration-300 group-hover:brightness-110"
                    style={
                      {
                        width: `${widthPct}%`,
                        background: 'var(--funnel-band-color)',
                        '--funnel-band-color': color,
                      } as CSSProperties
                    }
                  />
                )}
              </div>
              {activeIdx === idx && renderTooltip(step, idx)}
            </>
          )
          return (
            <div role="listitem" key={step.id}>
              {onStepClick ? (
                <button type="button" onClick={() => onStepClick(step)} {...rowProps} className={`${rowProps.className} cursor-pointer`}>
                  {rowContent}
                </button>
              ) : (
                <div role="group" tabIndex={0} {...rowProps}>
                  {rowContent}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
